// filepath: d:\develop\Joomla\easyforms\react-app\src\components\translations\FrontendLanguageSwitcher.tsx
// Frontend Language Switcher Component
// Premium, minimalist language selector for forms
import React, { useState, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { cn } from '@shondalai/ui-commons';
import { translationsService, type Language } from '../../services/translations.service';
import { useTranslation } from '../../contexts/translation-context';

interface FrontendLanguageSwitcherProps {
    className?: string;
    variant?: 'dropdown' | 'inline' | 'minimal';
}

export const FormLanguageSwitcher: React.FC<FrontendLanguageSwitcherProps> = ({
    className,
    variant = 'minimal',
}) => {
    const { currentLanguage, setLanguage } = useTranslation();
    const [languages, setLanguages] = useState<Record<string, Language>>({});
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const langs = await translationsService.getFormLanguages();
                setLanguages(langs);
            } catch (error) {
                console.error('Failed to load languages:', error);
            }
        };

        loadLanguages();
    }, [currentLanguage]);

    const languagesArray = Object.values(languages);

    // Don't show if less than 2 languages
    if (languagesArray.length < 2) {
        return null;
    }

    const currentLang = languages[currentLanguage];

    // Minimal variant - sleek, compact flag buttons
    if (variant === 'minimal') {
        return (
            <div className={cn('inline-flex items-center gap-1.5 rounded-lg bg-background/60 backdrop-blur-sm border border-border/40 p-1 shadow-sm', className)}>
                {languagesArray.map((lang) => (
                    <button
                        key={lang.lang_code}
                        onClick={() => setLanguage(lang.lang_code)}
                        className={cn(
                            'relative group flex items-center justify-center rounded-md transition-all duration-200',
                            'h-6 w-8 overflow-hidden',
                            currentLanguage === lang.lang_code
                                ? 'bg-foreground/5 ring-1 ring-foreground/10 shadow-sm'
                                : 'hover:bg-foreground/5'
                        )}
                        title={lang.title_native || lang.title}
                        aria-label={`Switch to ${lang.title_native || lang.title}`}
                    >
                        {lang.image ? (
                            <img
                                src={`/media/mod_languages/images/${lang.image}.gif`}
                                alt={lang.title}
                                className={cn(
                                    'object-cover rounded-sm transition-all duration-200',
                                    currentLanguage === lang.lang_code
                                        ? 'opacity-100 scale-100'
                                        : 'opacity-70 group-hover:opacity-100 group-hover:scale-105'
                                )}
                                onError={(e) => {
                                    const target = e.currentTarget;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        const span = document.createElement('span');
                                        span.className = 'text-[10px] font-semibold tracking-wide text-foreground/60';
                                        span.textContent = lang.sef.toUpperCase();
                                        parent.appendChild(span);
                                    }
                                }}
                            />
                        ) : (
                            <span className="text-[10px] font-semibold tracking-wide text-foreground/60">
                                {lang.sef.toUpperCase()}
                            </span>
                        )}
                        {currentLanguage === lang.lang_code && (
                            <div className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary shadow-sm" />
                        )}
                    </button>
                ))}
            </div>
        );
    }

    // Inline variant - compact buttons with subtle styling
    if (variant === 'inline') {
        return (
            <div className={cn('inline-flex items-center gap-2', className)}>
                {languagesArray.map((lang) => (
                    <button
                        key={lang.lang_code}
                        onClick={() => setLanguage(lang.lang_code)}
                        className={cn(
                            'group flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200',
                            'border border-transparent',
                            currentLanguage === lang.lang_code
                                ? 'bg-foreground/5 border-foreground/10'
                                : 'hover:bg-foreground/5 opacity-60 hover:opacity-100'
                        )}
                        title={lang.title_native || lang.title}
                    >
                        {lang.image && (
                            <img
                                src={`/media/mod_languages/images/${lang.image}.gif`}
                                alt={lang.title}
                                className="h-4 w-6 object-cover rounded-sm"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        )}
                        <span className="text-xs font-medium tracking-wide">
                            {lang.sef.toUpperCase()}
                        </span>
                    </button>
                ))}
            </div>
        );
    }

    // Dropdown variant - elegant dropdown menu
    return (
        <div className={cn('relative inline-block', className)}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'group flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200',
                    'bg-background/80 backdrop-blur-sm border border-border/50',
                    'hover:border-foreground/20 hover:bg-background',
                    'text-sm font-medium shadow-sm',
                    isOpen && 'border-foreground/20 bg-background shadow-md'
                )}
                aria-label="Select language"
            >
                {currentLang?.image ? (
                    <img
                        src={`/media/mod_languages/images/${currentLang.image}.gif`}
                        alt={currentLang.title}
                        className="h-4 w-6 object-cover rounded-sm"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                ) : (
                    <Globe className="h-4 w-4 text-foreground/60" />
                )}
                <span className="text-foreground/80 group-hover:text-foreground transition-colors">
                    {currentLang?.sef.toUpperCase() || 'Lang'}
                </span>
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className={cn(
                        'absolute right-0 mt-2 min-w-[180px] rounded-lg overflow-hidden',
                        'bg-background/95 backdrop-blur-md border border-border/50 shadow-xl z-50'
                    )}>
                        <div className="p-1">
                            {languagesArray.map((lang) => (
                                <button
                                    key={lang.lang_code}
                                    onClick={() => {
                                        setLanguage(lang.lang_code);
                                        setIsOpen(false);
                                    }}
                                    className={cn(
                                        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150',
                                        'hover:bg-foreground/5',
                                        currentLanguage === lang.lang_code
                                            ? 'bg-foreground/5 text-foreground'
                                            : 'text-foreground/70 hover:text-foreground'
                                    )}
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        {lang.image && (
                                            <img
                                                src={`/media/mod_languages/images/${lang.image}.gif`}
                                                alt={lang.title}
                                                className="h-4 w-6 object-cover rounded-sm flex-shrink-0"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        <span className="truncate font-medium">
                                            {lang.title_native || lang.title}
                                        </span>
                                    </div>
                                    {currentLanguage === lang.lang_code && (
                                        <Check className="h-3.5 w-3.5 text-foreground/60 flex-shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

